import { toast } from '@/hooks/use-toast';
import { useLogger } from '@/lib/logger';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';

export function useUserManagement() {
  const { t } = useTranslation('settings');
  const logger = useLogger();
  const utils = trpc.useContext();

  const createUser = trpc.users.create.useMutation({
    onMutate: async (payload) => {
      logger.info('Creating new user', {
        operation: 'create_user',
        email: payload.email,
        role: payload.role,
        locationCount: payload.locationIds?.length ?? 0,
      });
    },
    onSuccess: (data, _variables) => {
      logger.info('User created successfully', {
        operation: 'create_user',
        email: data.email,
      });

      utils.users.list.invalidate();
      const description = data.temporaryPassword
        ? `${t('toast.userCreated')}\nEmail: ${data.email}\nTemporary Password: ${data.temporaryPassword}`
        : `${t('toast.memberAddedDescription')}\nEmail: ${data.email}`;
      toast({ title: t('common:success'), description, duration: 10000 });
    },
    onError: (error, _variables) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'user-management',
          operation: 'create_user',
        },
      });

      logger.error('Failed to create user', {
        operation: 'create_user',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateUser = trpc.users.update.useMutation({
    onMutate: async (payload) => {
      logger.info('Updating user', {
        operation: 'update_user',
        userId: payload.userId,
        role: payload.role,
        locationCount: payload.locationIds?.length ?? 'unchanged',
      });
    },
    onSuccess: (_, variables) => {
      logger.info('User updated successfully', {
        operation: 'update_user',
        userId: variables.userId,
      });

      utils.users.list.invalidate();
      toast({
        title: t('common:success'),
        description: t('toast.userUpdated'),
      });
    },
    onError: (error, _variables) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'user-management',
          operation: 'update_user',
        },
      });

      logger.error('Failed to update user', {
        operation: 'update_user',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteUser = trpc.users.delete.useMutation({
    onMutate: async (payload) => {
      logger.info('Deleting user', {
        operation: 'delete_user',
        userId: payload.userId,
      });
    },
    onSuccess: (_, variables) => {
      logger.info('User deleted successfully', {
        operation: 'delete_user',
        userId: variables.userId,
      });

      utils.users.list.invalidate();
      toast({
        title: t('common:success'),
        description: t('toast.userDeleted'),
      });
    },
    onError: (error, _variables) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'user-management',
          operation: 'delete_user',
        },
      });

      logger.error('Failed to delete user', {
        operation: 'delete_user',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const toggleUserStatus = trpc.users.toggleStatus.useMutation({
    onMutate: async (payload) => {
      logger.info('Toggling user status', {
        operation: 'toggle_user_status',
        userId: payload.userId,
        newStatus: payload.isActive ? 'active' : 'inactive',
      });
    },
    onSuccess: (data, variables) => {
      logger.info('User status updated', {
        operation: 'toggle_user_status',
        userId: variables.userId,
        isActive: data.isActive,
      });

      utils.users.list.invalidate();
      toast({
        title: t('common:success'),
        description: t('toast.userStatusUpdated'),
      });
    },
    onError: (error, _variables) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'user-management',
          operation: 'toggle_user_status',
        },
      });

      logger.error('Failed to toggle user status', {
        operation: 'toggle_user_status',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const addMembershipByEmail = trpc.users.addMembershipByEmail.useMutation({
    onMutate: async (payload) => {
      logger.info('Adding existing user to org', {
        operation: 'add_membership_by_email',
        email: payload.email,
        role: payload.role,
        locationCount: payload.locationIds?.length ?? 0,
      });
    },
    onSuccess: () => {
      utils.users.list.invalidate();
      toast({ title: t('toast.memberAdded'), description: t('toast.memberAddedDescription') });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { feature: 'user-management', operation: 'add_membership_by_email' },
      });
      logger.error('Failed to add membership', { operation: 'add_membership_by_email', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const removeMembership = trpc.users.removeMembership.useMutation({
    onMutate: async (payload) => {
      logger.info('Removing user from org', {
        operation: 'remove_membership',
        userId: payload.userId,
      });
    },
    onSuccess: () => {
      utils.users.list.invalidate();
      toast({ title: t('toast.memberRemoved'), description: t('toast.memberRemovedDescription') });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { feature: 'user-management', operation: 'remove_membership' },
      });
      logger.error('Failed to remove membership', { operation: 'remove_membership', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    addMembershipByEmail,
    removeMembership,
  };
}
